import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  IconButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { FORMS_DATA_SETTINGS } from "../../config/urls";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import PageLoader from "../../common/page-loader";

const ASK = "ask";
const CUSTOM = "custom";
const NONE = "__none";
const MODE_OPTIONS = [
  { label: "Overwrite", value: "overwrite" },
  { label: "Keep existing", value: "keep" },
];
const KIND_LABELS = {
  text: "text",
  email: "email",
  phone: "phone",
  number: "number",
  date: "date",
  list: "multiple choice",
  boolean: "opt-in",
};

// Delivery options that aren't available yet; shown so the layout is final.
const COMING_SOON_SECTIONS = [
  { title: "Also send to", summary: "Webhook, Google Sheets, HubSpot, Notify team" },
  { title: "Test submission", summary: "Try sample answers" },
  { title: "How answers come back", summary: "Standard" },
  { title: "Privacy", summary: "Encryption, Inbox visibility, retention" },
  { title: "Stored submission record", summary: "JSON preview" },
];

const emptyMapping = {
  source: ASK,
  custom_value: "",
  destination: "",
  mode: "overwrite",
  editable: true,
  sensitive: false,
};

const tableOf = (ref) => String(ref || "").split(".")[0];

// "Your_name_284d7c" / "Your name" -> "your_name"
const normalizeName = (value) =>
  String(value || "")
    .replace(/_[0-9a-zA-Z]{6}$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * Data & destinations tab of a form: the tables it reads from, and per field where
 * its starting value comes from and where its answer is saved.
 * `onControlsChange({ dirty, saving, save })` lets the page header drive Save changes.
 */
const FormDataDestinations = ({ formId, onControlsChange }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "error" });
  const [fields, setFields] = useState([]);
  const [tables, setTables] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [mappings, setMappings] = useState({});
  const [editable, setEditable] = useState(false);
  const [saved, setSaved] = useState("");
  const [sourceWarning, setSourceWarning] = useState("");
  const [menu, setMenu] = useState({ anchor: null, key: null });

  const snapshot = JSON.stringify({ dataSources, mappings });
  const dirty = !loading && snapshot !== saved;

  const tableFields = {};
  tables.forEach((t) => t.fields.forEach((f) => (tableFields[f.key] = { ...f, table: t })));
  const tableLabel = (key) => tables.find((t) => t.key === key)?.label || key;
  const fillTables = dataSources.filter((s) => s.fill).map((s) => s.table);
  const saveTables = dataSources.filter((s) => s.save).map((s) => s.table);
  const refLabel = (ref) =>
    tableFields[ref] ? `${tableFields[ref].table.label} › ${tableFields[ref].label}` : ref;
  const accepts = (ref, kind) =>
    tableFields[ref] && (tableFields[ref].kind === "text" || tableFields[ref].kind === kind);

  // Inline errors per field; any of them blocks saving.
  const errors = {};
  const usedBy = {};
  fields.forEach((f) => {
    const m = mappings[f.key];
    if (!m) return;
    const fieldErrors = {};
    if (m.source === CUSTOM && !m.custom_value.trim()) {
      fieldErrors.custom = "Enter a value.";
    }
    if (m.destination) {
      if (!saveTables.includes(tableOf(m.destination))) {
        fieldErrors.destination = `${tableLabel(tableOf(m.destination))} isn't selected as the destination table.`;
      } else if (!accepts(m.destination, f.kind)) {
        fieldErrors.destination = `Type doesn't match: this field's answers are ${
          KIND_LABELS[f.kind] || f.kind
        }, ${refLabel(m.destination)} takes ${KIND_LABELS[tableFields[m.destination]?.kind] || "another type"}.`;
      } else if (usedBy[m.destination]) {
        fieldErrors.destination = `${usedBy[m.destination]} already saves here.`;
      } else {
        usedBy[m.destination] = f.label || f.key;
      }
    }
    if (Object.keys(fieldErrors).length) errors[f.key] = fieldErrors;
  });
  const hasErrors = Object.keys(errors).length > 0;

  const getHeaders = useCallback(async () => {
    const token = await tokenRef.current();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [tenant]);

  const applyResponse = (data) => {
    setFields(data.fields);
    setTables(data.tables);
    setDataSources(data.data_sources);
    setEditable(data.editable);
    const full = {};
    data.fields.forEach((f) => {
      full[f.key] = { ...emptyMapping, ...(data.mappings[f.key] || {}) };
    });
    setMappings(full);
    setSourceWarning("");
    setSaved(JSON.stringify({ dataSources: data.data_sources, mappings: full }));
  };

  const readError = async (response, fallback) => {
    const data = await response.json().catch(() => ({}));
    return data?.error || data?.detail || fallback;
  };

  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const response = await fetch(FORMS_DATA_SETTINGS(formId), {
          headers: await getHeaders(),
        });
        if (!response.ok) throw new Error(await readError(response, "Failed to load settings"));
        applyResponse(await response.json());
      } catch (error) {
        setAlert({ message: error.message || "Failed to load settings", severity: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const save = useCallback(async () => {
    if (hasErrors) {
      setAlert({ message: "Fix the highlighted fields before saving.", severity: "error" });
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(FORMS_DATA_SETTINGS(formId), {
        method: "PUT",
        headers: await getHeaders(),
        body: JSON.stringify({ data_sources: dataSources, mappings }),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to save"));
      applyResponse(await response.json());
      setAlert({ message: "Changes saved", severity: "success" });
    } catch (error) {
      setAlert({ message: error.message || "Failed to save", severity: "error" });
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, dataSources, mappings, hasErrors, getHeaders]);

  useEffect(() => {
    onControlsChange?.({ dirty: dirty && editable, saving, save });
  }, [dirty, editable, saving, save, onControlsChange]);

  const updateMapping = (key, patch) =>
    setMappings((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // Turning off a table's Fill from (or removing it) sends the fields that filled from
  // it back to Ask in chatbot; turning off its Save to sends the fields saved to it
  // back to Form submissions only.
  const applySources = (next) => {
    const nextFill = next.filter((s) => s.fill).map((s) => s.table);
    const nextSave = next.filter((s) => s.save).map((s) => s.table);
    const asked = fields.filter((f) => {
      const t = tableOf(mappings[f.key]?.source);
      return fillTables.includes(t) && !nextFill.includes(t);
    });
    const unsaved = fields.filter((f) => {
      const t = tableOf(mappings[f.key]?.destination);
      return saveTables.includes(t) && !nextSave.includes(t);
    });
    if (asked.length || unsaved.length) {
      setMappings((prev) => {
        const out = { ...prev };
        asked.forEach((f) => (out[f.key] = { ...out[f.key], source: ASK }));
        unsaved.forEach((f) => (out[f.key] = { ...out[f.key], destination: "" }));
        return out;
      });
      const names = (list) => list.map((f) => f.label || f.key).join(", ");
      setSourceWarning(
        [
          asked.length && `${names(asked)} will be asked in the chatbot.`,
          unsaved.length && `${names(unsaved)} will be saved to form submissions only.`,
        ]
          .filter(Boolean)
          .join(" "),
      );
    }
    setDataSources(next);
  };

  // The Fill from and Save to pickers are independent: `keys` replaces one use's
  // tables and the other use's are kept.
  const changeUse = (use, keys) => {
    const other = use === "fill" ? "save" : "fill";
    const otherKeys = use === "fill" ? saveTables : fillTables;
    applySources(
      tables
        .map((t) => t.key)
        .filter((table) => keys.includes(table) || otherKeys.includes(table))
        .map((table) => ({
          table,
          [use]: keys.includes(table),
          [other]: otherKeys.includes(table),
        })),
    );
  };

  // Matches each unmapped field to a field of a Save to table by name, else by type
  // (email, date, number). A match in a Fill from table also prefills from it.
  const autoMapAll = () => {
    const candidates = saveTables.flatMap((t) => tables.find((x) => x.key === t)?.fields || []);
    const used = new Set(Object.values(mappings).map((m) => m.destination).filter(Boolean));
    const next = { ...mappings };
    fields.forEach((f) => {
      if (next[f.key]?.destination) return;
      const names = [normalizeName(f.label), normalizeName(f.key)];
      const free = candidates.filter((c) => !used.has(c.key) && accepts(c.key, f.kind));
      const match =
        free.find(
          (c) =>
            names.includes(normalizeName(c.key.split(".")[1])) ||
            names.includes(normalizeName(c.label)),
        ) ||
        (["email", "date", "number"].includes(f.kind) &&
          free.find((c) => c.kind === f.kind));
      if (!match) return;
      used.add(match.key);
      const prefill =
        next[f.key].source === ASK &&
        ["short_answer", "paragraph"].includes(f.type) &&
        fillTables.includes(tableOf(match.key));
      next[f.key] = {
        ...next[f.key],
        destination: match.key,
        ...(prefill ? { source: match.key } : {}),
      };
    });
    setMappings(next);
  };

  // Menu items for the given tables, grouped: a header item, then the table's fields.
  const groupedItems = (tableKeys) =>
    tables
      .filter((t) => tableKeys.includes(t.key))
      .flatMap((t) => [
        { header: true, label: t.label, value: `__header_${t.key}` },
        ...t.fields.map((f) => ({ label: f.label, value: f.key })),
      ]);

  const renderGroupedItem = (item, isSelected, onSelect) =>
    item.header ? (
      <ListSubheader
        key={item.value}
        sx={{ ...typography.bodySmall, lineHeight: "32px", color: grayFontColor }}
      >
        {item.label}
      </ListSubheader>
    ) : (
      <MenuItem
        key={item.value}
        onClick={() => onSelect(item.value, item)}
        sx={{ ...typography.bodyLarge, pl: item.value.includes(".") ? 3 : 2 }}
      >
        {item.label}
        {isSelected && <CheckIcon sx={{ ml: 1 }} color="success" />}
      </MenuItem>
    );

  const bodyText = { ...typography.bodyLarge, color: "#1D2330" };
  const errorText = { ...typography.bodySmall, color: "#b50000", maxWidth: 260 };
  const dropdownSx = { minWidth: 220, justifyContent: "space-between" };

  const columns = [
    {
      id: "field",
      label: "Field",
      render: (f) => (
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography sx={bodyText}>{f.label || f.key}</Typography>
            {mappings[f.key]?.sensitive && (
              <LockOutlinedIcon
                titleAccess="Sensitive"
                sx={{ fontSize: 16, color: grayFontColor }}
              />
            )}
          </Stack>
          <Box>
            <AppChip label={f.key} sx={{ height: 20, backgroundColor: dashboardCardBgColor }} />
          </Box>
        </Stack>
      ),
    },
    {
      id: "source",
      label: "Value source",
      render: (f) => {
        const m = mappings[f.key] || emptyMapping;
        const label =
          m.source === ASK ? "Ask in chatbot" : m.source === CUSTOM ? "Custom value" : refLabel(m.source);
        return (
          <Stack spacing={0.75}>
            <AppDropdown
              disabled={!editable}
              searchable
              menuItems={[
                { label: "Ask in chatbot", value: ASK },
                { label: "Custom value", value: CUSTOM },
                // WhatsApp can only prefill short answers and paragraphs.
                ...(["short_answer", "paragraph"].includes(f.type) ? groupedItems(fillTables) : []),
              ]}
              renderItem={renderGroupedItem}
              selectedValue={m.source}
              onMenuSelect={(value) => updateMapping(f.key, { source: value })}
              endIcon={<ExpandMore />}
              sx={dropdownSx}
            >
              {label}
            </AppDropdown>
            {m.source === CUSTOM && (
              <TextField
                size="small"
                disabled={!editable}
                value={m.custom_value}
                placeholder="Value"
                error={Boolean(errors[f.key]?.custom)}
                helperText={errors[f.key]?.custom || "Saved on submit, whatever the user enters."}
                inputProps={{ "aria-label": `Custom value for ${f.label || f.key}`, maxLength: 255 }}
                onChange={(e) => updateMapping(f.key, { custom_value: e.target.value })}
                sx={{ maxWidth: 260 }}
              />
            )}
          </Stack>
        );
      },
    },
    {
      id: "destination",
      label: "Destination",
      render: (f) => {
        const m = mappings[f.key] || emptyMapping;
        return (
          <Stack spacing={0.5}>
            <AppDropdown
              disabled={!editable}
              searchable
              menuItems={[
                { label: "Form submissions only", value: "" },
                ...groupedItems(saveTables),
              ]}
              renderItem={renderGroupedItem}
              selectedValue={m.destination}
              onMenuSelect={(value) => updateMapping(f.key, { destination: value })}
              endIcon={<ExpandMore />}
              sx={{
                ...dropdownSx,
                ...(errors[f.key]?.destination && {
                  borderColor: `${ColorsConstants.error} !important`,
                }),
              }}
            >
              {m.destination ? refLabel(m.destination) : "Form submissions only"}
            </AppDropdown>
            {errors[f.key]?.destination && (
              <Typography sx={errorText}>{errors[f.key].destination}</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: "mode",
      label: "If value exists",
      render: (f) => (
        <AppDropdown
          disabled={!editable || !mappings[f.key]?.destination}
          menuItems={MODE_OPTIONS}
          selectedValue={mappings[f.key]?.mode || "overwrite"}
          onMenuSelect={(value) => updateMapping(f.key, { mode: value })}
          endIcon={<ExpandMore />}
          sx={{ minWidth: 160, justifyContent: "space-between" }}
        >
          {MODE_OPTIONS.find((o) => o.value === (mappings[f.key]?.mode || "overwrite"))?.label}
        </AppDropdown>
      ),
    },
    {
      id: "more",
      label: "",
      render: (f) => (
        <IconButton
          size="small"
          disabled={!editable}
          aria-label={`More options for ${f.label || f.key}`}
          onClick={(e) => setMenu({ anchor: e.currentTarget, key: f.key })}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const menuMapping = mappings[menu.key] || emptyMapping;

  if (loading) return <PageLoader />;

  return (
    <Box>
      {alert.message && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ message: "", severity: "error" })}
          sx={{ mb: 2, "& .MuiAlert-message": typography.bodyLarge }}
        >
          {alert.message}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box sx={{ p: 2.25, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ ...typography.h5, fontSize: 16 }}>
              Fill and save answers
            </Typography>
            <Box
              component="button"
              type="button"
              disabled={!editable || fields.length === 0}
              onClick={autoMapAll}
              sx={{
                ...typography.bodySmall,
                background: "none",
                border: 0,
                p: 0,
                cursor: "pointer",
                color: ColorsConstants.primaryPurple100,
                "&:disabled": { color: grayFontColor, cursor: "default" },
              }}
            >
              Auto-map all
            </Box>
          </Stack>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
            Every submission is always stored in BotBridge. Rows are matched on the sender's
            WhatsApp number (the most recent if several match).
          </Typography>

          <Stack direction="row" spacing={3} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {[
              ["fill", "Source table", fillTables],
              ["save", "Destination table", saveTables],
            ].map(([use, label, selected]) => (
              <Stack key={use} spacing={0.5}>
                <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                  {label}
                </Typography>
                <AppDropdown
                  disabled={!editable}
                  menuItems={[
                    { label: "None", value: NONE },
                    ...tables.map((t) => ({ label: t.label, value: t.key })),
                  ]}
                  selectedValue={selected[0] || NONE}
                  onMenuSelect={(value) => changeUse(use, value === NONE ? [] : [value])}
                  endIcon={<ExpandMore />}
                  sx={{ width: 320, justifyContent: "space-between" }}
                >
                  {tableLabel(selected[0]) || "None"}
                </AppDropdown>
              </Stack>
            ))}
          </Stack>

          {sourceWarning && (
            <Alert
              severity="warning"
              onClose={() => setSourceWarning("")}
              sx={{ mt: 1.5, "& .MuiAlert-message": typography.bodySmall }}
            >
              {sourceWarning}
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 1.5, "& .MuiAlert-message": typography.bodySmall }}>
            Changes apply from the next form session. Only short answers and paragraphs can
            be filled from a table; other fields are asked in the chatbot.
          </Alert>
        </Box>

        {!loading && fields.length === 0 ? (
          <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, p: 2.25 }}>
            Add input fields in Build screens to map them here.
          </Typography>
        ) : (
          <AppTable
            columns={columns}
            rows={fields}
            rowKey="key"
            loading={loading}
            maxHeight={500}
            autoAdjustHeight={false}
            headCellSx={{
              ...typography.bodySmall,
              backgroundColor: dashboardCardBgColor,
              color: grayFontColor,
              padding: "9px 18px",
              borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            }}
            bodyCellSx={{ padding: "10px 18px", verticalAlign: "top" }}
            rowSx={() => ({
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#FFFFFF" },
              "& td": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderTop: "none",
              },
              "&:last-child td": { borderBottom: "none" },
            })}
            paperSx={{ border: "none", borderRadius: 0, boxShadow: "none", mb: 0 }}
          />
        )}
      </Box>

      <Menu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={() => setMenu({ anchor: null, key: null })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled={menuMapping.source === ASK || menuMapping.source === CUSTOM}
          onClick={() => updateMapping(menu.key, { editable: !menuMapping.editable })}
        >
          <Checkbox edge="start" size="small" checked={menuMapping.editable} disableRipple />
          <ListItemText
            primary="Let user edit prefilled value"
            secondary={
              menuMapping.source === ASK || menuMapping.source === CUSTOM
                ? "Only for fields filled from a data source"
                : "When off, the data source value is kept on submit"
            }
            primaryTypographyProps={{ sx: typography.bodyLarge }}
            secondaryTypographyProps={{ sx: typography.bodySmall }}
          />
        </MenuItem>
        <MenuItem onClick={() => updateMapping(menu.key, { sensitive: !menuMapping.sensitive })}>
          <Checkbox edge="start" size="small" checked={menuMapping.sensitive} disableRipple />
          <ListItemText
            primary="Sensitive"
            secondary="Masked in Submissions and exports"
            primaryTypographyProps={{ sx: typography.bodyLarge }}
            secondaryTypographyProps={{ sx: typography.bodySmall }}
          />
        </MenuItem>
      </Menu>

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        {COMING_SOON_SECTIONS.map((section) => (
          <Accordion
            key={section.title}
            disabled
            disableGutters
            square
            elevation={0}
            sx={{
              "&:not(:last-child)": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              },
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.25 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>
                  {section.title}
                </Typography>
                <AppChip label="Coming soon" sx={{ height: 20 }} />
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: grayFontColor,
                    flex: 1,
                    textAlign: "right",
                    pr: 1,
                  }}
                  noWrap
                >
                  {section.summary}
                </Typography>
              </Stack>
            </AccordionSummary>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default FormDataDestinations;
